import { useToast } from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { uploadShipToCsv } from '../api';
import { TARGET_VOUCHER_OPTIONS } from '../constants';
import {
  DEFAULT_ACQUISITION_CONFIGURATION,
  DEFAULT_TARGET_CONFIGURATION,
  TARGET_TAB_TO_API_TYPE,
} from '../constants/voucher-target-form.constants';
import type { CreateVoucherRequest } from '../types';
import { downloadShipToTemplate } from '../utils/voucher-target-form.utils';

type TargetFieldKey =
  | 'business_segmentations'
  | 'distribution_channels'
  | 'business_categories';

interface UseVoucherTargetFormParams {
  form: UseFormReturn<CreateVoucherRequest>;
}

export const useVoucherTargetForm = ({ form }: UseVoucherTargetFormParams) => {
  const { watch, setValue } = form;
  const session = useAuthStore();
  const toast = useToast();

  const [isCsvUploading, setIsCsvUploading] = useState(false);
  const [csvUploadUrl, setCsvUploadUrl] = useState<string | null>(null);
  const [csvUploadStats, setCsvUploadStats] = useState<{
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
  } | null>(null);

  const targetConfiguration = watch('target_configuration');
  const acquisitionConfiguration = watch('acquisition_configuration');

  useEffect(() => {
    if (!targetConfiguration) {
      setValue('target_configuration', { ...DEFAULT_TARGET_CONFIGURATION });
    }
  }, [targetConfiguration, setValue]);

  useEffect(() => {
    if (!acquisitionConfiguration) {
      setValue('acquisition_configuration', {
        ...DEFAULT_ACQUISITION_CONFIGURATION,
      });
    }
  }, [acquisitionConfiguration, setValue]);

  const targetType = targetConfiguration?.target_type ?? 'GENERAL';

  const targetTabIndex = useMemo(() => {
    return Math.max(
      0,
      TARGET_VOUCHER_OPTIONS.findIndex(
        (option) => TARGET_TAB_TO_API_TYPE[option.value] === targetType
      )
    );
  }, [targetType]);

  const selectedCustomerTypes =
    targetConfiguration?.business_segmentations ?? [];
  const selectedBranches = targetConfiguration?.distribution_channels ?? [];
  const selectedRestaurantCategories =
    targetConfiguration?.business_categories ?? [];

  const handleTargetTabChange = useCallback(
    (index: number) => {
      const option = TARGET_VOUCHER_OPTIONS[index];
      if (!option) return;

      setValue(
        'target_configuration.target_type',
        TARGET_TAB_TO_API_TYPE[option.value],
        {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        }
      );
    },
    [setValue]
  );

  const createMultiSelectChangeHandler = useCallback(
    (field: TargetFieldKey) =>
      (options: ReadonlyArray<SelectOption> | null) => {
        const codes = Array.from(options ?? [], (option) => option.value);
        setValue(`target_configuration.${field}` as const, codes, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      },
    [setValue]
  );

  const handleCustomerTypesChange = createMultiSelectChangeHandler(
    'business_segmentations'
  );
  const handleBranchesChange = createMultiSelectChangeHandler(
    'distribution_channels'
  );
  const handleRestaurantCategoriesChange = createMultiSelectChangeHandler(
    'business_categories'
  );

  const handleCsvUpload = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      setIsCsvUploading(true);

      try {
        const response = await uploadShipToCsv(session.access_token, file);

        setCsvUploadUrl(response.url);
        setCsvUploadStats({
          total_rows: response.total_rows,
          valid_rows: response.valid_rows,
          invalid_rows: response.invalid_rows,
        });

        // Set files to form state
        setValue('shipToCsv', files, {
          shouldValidate: true,
          shouldDirty: true,
        });

        // Set CSV URL to target_configuration for API submission
        setValue('target_configuration.shipto_csv_url', response.url, {
          shouldValidate: true,
          shouldDirty: true,
        });

        const message =
          response.invalid_rows > 0
            ? `CSV berhasil diupload. ${response.valid_rows} rows valid dari ${response.total_rows} total`
            : `CSV berhasil diupload. ${response.valid_rows} rows valid`;

        showToast(toast, 'success', message);
      } catch (error: unknown) {
        let errorMessage = 'Gagal mengupload CSV';
        if (isAxiosError(error)) {
          errorMessage =
            (error.response?.data as { message?: string } | undefined)
              ?.message || error.message;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        showToast(toast, 'error', errorMessage);

        setValue('shipToCsv', []);
        setValue('target_configuration.shipto_csv_url', null);
        setCsvUploadUrl(null);
        setCsvUploadStats(null);
      } finally {
        setIsCsvUploading(false);
      }
    },
    [session.access_token, setValue, toast]
  );

  const handleShipToRemove = useCallback(() => {
    setCsvUploadUrl(null);
    setCsvUploadStats(null);
    setValue('shipToCsv', [], {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    // Clear CSV URL from target_configuration
    setValue('target_configuration.shipto_csv_url', null, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  }, [setValue]);

  const handleDownloadShipToTemplate = useCallback(
    () => downloadShipToTemplate(),
    []
  );

  return {
    targetType,
    targetTabIndex,
    selectedCustomerTypes,
    selectedBranches,
    selectedRestaurantCategories,
    isCsvUploading,
    csvUploadUrl,
    csvUploadStats,
    handleTargetTabChange,
    handleCustomerTypesChange,
    handleBranchesChange,
    handleRestaurantCategoriesChange,
    handleCsvUpload,
    handleShipToRemove,
    handleDownloadShipToTemplate,
  };
};
